// Pure logic for the Crossover Solo panel — extracted so it's
// independently unit-testable without rendering the .tsx component.
//
// The panel UI imports these helpers; tests import them directly.

import type { MultiviewBusSource } from "./multiview-co-160a";

export type CrossoverSourceId =
  | "black"
  | "cam1"
  | "cam2"
  | "cam3"
  | "media1"
  | "media2";

export interface CrossoverSourceDef {
  id: CrossoverSourceId;
  label: string;
  /** Bus-button position on the real hardware (0=BLACK MATTE, 1-8 = INPUT 1-8). */
  slot: number;
}

/**
 * The CO-160a roster mapped onto Ross Crossover Solo physical bus slots.
 *
 *   Slot 0  BLACK MATTE        (always present, no input)
 *   Slot 1  INPUT 1            CAM 1
 *   Slot 2  INPUT 2            CAM 2
 *   Slot 3  INPUT 3            CAM 3
 *   Slot 4  INPUT 4            (unassigned for this convention)
 *   Slot 5  INPUT 5            (unassigned)
 *   Slot 6  INPUT 6            (unassigned)
 *   Slot 7  INPUT 7 / MEDIA 1  MEDIA 1
 *   Slot 8  INPUT 8 / MEDIA 2  MEDIA 2
 *   Slot 9  SHIFT              (engages /9-12 alternates on slots 1-4
 *                               and MEDIA 1/2 on slots 7-8)
 */
export const CROSSOVER_SOURCES: CrossoverSourceDef[] = [
  { id: "black", label: "BLACK", slot: 0 },
  { id: "cam1", label: "CAM 1", slot: 1 },
  { id: "cam2", label: "CAM 2", slot: 2 },
  { id: "cam3", label: "CAM 3", slot: 3 },
  { id: "media1", label: "MEDIA 1", slot: 7 },
  { id: "media2", label: "MEDIA 2", slot: 8 },
];

/**
 * Map a numeric keyboard key onto a CO-160a source for the PGM bus.
 * Slots without a CO-160a assignment (4, 5, 6) are intentionally absent —
 * pressing those keys is a no-op, mirroring the real "input present but
 * unassigned" hardware behavior.
 */
export const PGM_KEY_MAP: Record<string, CrossoverSourceId> = {
  "0": "black",
  "1": "cam1",
  "2": "cam2",
  "3": "cam3",
  "7": "media1",
  "8": "media2",
};

/**
 * Map a Q-row key onto a CO-160a source for the PVW bus. Each letter sits
 * physically above its number-row counterpart on a US keyboard (q above 1,
 * u above 7, etc.) so muscle memory carries from PGM down-row to PVW
 * up-row.
 */
export const PVW_KEY_MAP: Record<string, CrossoverSourceId> = {
  "`": "black",
  q: "cam1",
  w: "cam2",
  e: "cam3",
  u: "media1",
  i: "media2",
};

export function isCrossoverSourceId(v: string | undefined): v is CrossoverSourceId {
  return Boolean(v && CROSSOVER_SOURCES.some((s) => s.id === v));
}

/** Translate a Crossover Solo source id onto the multiview's bus-source type. */
export function toMultiviewSource(id: CrossoverSourceId): MultiviewBusSource {
  return id;
}

// ---------- Engine action shape ----------

/**
 * Action emitted by the panel when a control fires. The scenario runner
 * translates these into `simulator-event-log` events.
 */
export type CrossoverSoloAction =
  | { kind: "arm"; target: CrossoverSourceId; clean: null; ts_ms: number }
  | {
      kind: "cut" | "auto" | "diss" | "wipe" | "dve";
      target: CrossoverSourceId;
      clean: boolean;
      ts_ms: number;
    }
  | { kind: "key_cut" | "key_auto"; key_index: 1 | 2 | 3; ts_ms: number }
  | { kind: "ftb" | "dsk"; ts_ms: number }
  // T-bar manual transition. `progress` is throttled while dragging;
  // `tbar_partial` fires once on release if the operator let go without
  // completing the take. The terminal `auto` event fires on commit
  // (progress reaches 1.0), giving cleanTake/double-tap drills the
  // same shape they get from the AUTO TRANS button.
  | { kind: "tbar_progress"; value: number; ts_ms: number }
  | { kind: "tbar_partial"; progress: number; ts_ms: number };

/**
 * Pure check for whether a CUT or AUTO would be a "clean take" given the
 * panel's last-action timestamp + current PGM/PVW. Used by both the panel
 * and tests.
 */
export function isCleanTake(
  now: number,
  lastActionMs: number,
  pgm: CrossoverSourceId,
  pvw: CrossoverSourceId,
  doubleTapMs = 400,
): boolean {
  const isDoubleTap = now - lastActionMs < doubleTapMs;
  const isCollision = pgm === pvw;
  return !isDoubleTap && !isCollision;
}

/** Resolve a keyboard event into the panel action it should trigger. */
export type CrossoverShortcut =
  | { kind: "pgm"; target: CrossoverSourceId }
  | { kind: "pvw"; target: CrossoverSourceId }
  | { kind: "cut" }
  | { kind: "auto" }
  | { kind: "key_auto"; key_index: 1 | 2 | 3 }
  | null;

export function shortcutFor(
  key: string,
  shiftKey: boolean,
  activeKey: 1 | 2 | 3,
): CrossoverShortcut {
  if (PGM_KEY_MAP[key]) return { kind: "pgm", target: PGM_KEY_MAP[key] };
  const lk = key.toLowerCase();
  if (PVW_KEY_MAP[lk]) return { kind: "pvw", target: PVW_KEY_MAP[lk] };
  if (key === "Enter") return { kind: "cut" };
  if (key === " ") return { kind: "auto" };
  if (shiftKey && lk === "k") return { kind: "key_auto", key_index: activeKey };
  return null;
}
